<script setup lang="ts">
import type {
  ColDef,
  GridReadyEvent,
  ICellRendererParams,
  PostSortRowsParams,
  RowClassParams,
} from "ag-grid-community";
import { storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";
import AppGrid from "../../components/AppGrid.vue";
import {
  FITTABLE_DEFAULT_COL_DEF,
  GRID_SELECTION_COLUMN_DEF,
} from "../../components/composables/useGridColumnDefaults.ts";
import type { IntermediateOrderSkuRow } from "../../redistribution/models/IntermediateOrderSkuRow.ts";
import { useAppStore } from "../../stores/app.ts";
import { formatUsDate } from "../../utils/dateFormatting.ts";
import { useGridRowManagement } from "../home/composables/useGridRowManagement.ts";
import IntermediateOrderDetailRenderer from "./IntermediateOrderDetailRenderer.vue";
import {
  DETAIL_GRID_ROW_HEIGHT,
  MAX_DETAIL_VISIBLE_ROWS,
  ROW_HEIGHT,
} from "./intermediateOrderGridConfig.ts";
import type { IntermediateOrderGridRow } from "./intermediateOrderRows.ts";
import {
  buildIntermediateOrderRows,
  isDetailRow,
} from "./intermediateOrderRows.ts";

const props = defineProps<{
  rows: IntermediateOrderSkuRow[] | undefined;
  loading?: boolean;
  // Set when the last search/refresh failed; distinct from "ran and found
  // nothing" (empty rows) so the grid can tell the user what actually happened.
  error?: string | undefined;
}>();

const emit = defineEmits<{
  refresh: [];
  selectionChanged: [selectedOrderNumbers: string[]];
}>();

const hasSearched = computed(() => props.rows !== undefined);
const selectedOrderKeys = ref<string[]>([]);
const { warehouseOptions } = storeToRefs(useAppStore());

// One grid row per Intermediate Order; its SKUs are nested behind "View Items".
const orderRows = computed(() =>
  buildIntermediateOrderRows(props.rows, warehouseOptions.value)
);

const {
  expandedPOs,
  gridApi,
  resetExpandedRows,
  isFullWidthRow,
  getRowId,
  getRowHeight,
  toggleDetail,
} = useGridRowManagement<IntermediateOrderGridRow>({
  rowHeight: ROW_HEIGHT,
  detailRowHeight: DETAIL_GRID_ROW_HEIGHT,
  maxDetailVisibleRows: MAX_DETAIL_VISIBLE_ROWS,
});

// A new search invalidates the previous result set's expansion state.
watch(() => props.rows, resetExpandedRows);

// Detail rows are injected directly after their parent; postSortRows keeps them
// there once the user sorts by a parent column.
const rowData = computed<IntermediateOrderGridRow[]>(() => {
  const rows: IntermediateOrderGridRow[] = [];
  for (const order of orderRows.value) {
    rows.push(order);
    if (expandedPOs.value.has(order.rowKey)) {
      rows.push({
        _isDetail: true,
        parentRowKey: order.rowKey,
        intermediatePoNumber: order.intermediatePoNumber,
        skus: order.skus,
      });
    }
  }
  return rows;
});

function postSortRows(params: PostSortRowsParams<IntermediateOrderGridRow>) {
  const parentNodes: typeof params.nodes = [];
  const detailByParent = new Map<string, (typeof params.nodes)[number]>();

  for (const node of params.nodes) {
    const data = node.data;
    if (isDetailRow(data)) {
      detailByParent.set(data.parentRowKey, node);
      continue;
    }
    parentNodes.push(node);
  }

  params.nodes.length = 0;

  for (const node of parentNodes) {
    params.nodes.push(node);
    const data = node.data;
    const detailNode =
      data && !isDetailRow(data) ? detailByParent.get(data.rowKey) : undefined;
    if (detailNode) {
      params.nodes.push(detailNode);
    }
  }
}

function onGridReady(event: GridReadyEvent<IntermediateOrderGridRow>): void {
  gridApi.value = event.api;
}

function onSelectionChanged(): void {
  if (!gridApi.value) {
    selectedOrderKeys.value = [];
    emit("selectionChanged", []);
    return;
  }

  const nextSelectedOrderKeys = [
    ...new Set(
      gridApi.value.getSelectedRows().flatMap((row) => {
        if (isDetailRow(row)) {
          return [];
        }
        return [row.rowKey];
      })
    ),
  ];

  selectedOrderKeys.value = nextSelectedOrderKeys;
  emit("selectionChanged", nextSelectedOrderKeys);
}

// Marks the first order row of each new BO# group so a divider can be drawn
// between groups - AppGrid does not expose ag-grid's native row-grouping
// options. Walks back past the previous order's detail row when one is open.
function getRowClass(
  params: RowClassParams<IntermediateOrderGridRow>
): string | undefined {
  const data = params.data;
  if (!data || isDetailRow(data)) {
    return undefined;
  }

  const rowIndex = params.node.rowIndex;
  if (rowIndex === null || rowIndex === 0) {
    return undefined;
  }

  for (let index = rowIndex - 1; index >= 0; index--) {
    const previous = params.api.getDisplayedRowAtIndex(index)?.data;
    if (!previous || isDetailRow(previous)) {
      continue;
    }
    return previous.bulkOrderNumber === data.bulkOrderNumber
      ? undefined
      : "io-grid-group-start-row";
  }

  return undefined;
}

function formatNullableDate(value: string | null): string {
  return value ? formatUsDate(value) : "—";
}

const DISTRIBUTION_STATUS_BADGE_CLASS: Record<string, string> = {
  "Pending Allocation": "dashboard-status-badge--moderate",
  "In Distribution": "dashboard-status-badge--balanced",
  Distributed: "dashboard-status-badge--healthy",
  "On Hold": "dashboard-status-badge--neutral",
};

function distributionStatusRenderer(params: { value: string }): HTMLElement {
  const cssClass =
    DISTRIBUTION_STATUS_BADGE_CLASS[params.value] ??
    "dashboard-status-badge--neutral";
  const span = document.createElement("span");
  span.className = `dashboard-status-badge ${cssClass}`;
  span.textContent = params.value;
  return span;
}

function detailsToggleRenderer(
  params: ICellRendererParams<IntermediateOrderGridRow>
): HTMLElement | string {
  const data = params.data;
  if (!data || isDetailRow(data)) {
    return "";
  }

  const isExpanded = expandedPOs.value.has(data.rowKey);
  const button = document.createElement("button");
  button.type = "button";
  button.className = "intermediate-order-details-toggle";
  button.textContent = isExpanded ? "View Items ▴" : "View Items ▾";
  button.setAttribute("aria-expanded", String(isExpanded));
  button.setAttribute(
    "aria-label",
    `${isExpanded ? "Hide" : "View"} ${String(data.skuCount)} SKUs for Intermediate Order ${data.intermediatePoNumber}`
  );
  button.addEventListener("click", (event) => {
    event.stopPropagation();
    toggleDetail(data.rowKey);
  });
  return button;
}

const columnDefs: ColDef<IntermediateOrderGridRow>[] = [
  {
    field: "bulkOrderNumber",
    headerName: "BO # (Parent)",
    minWidth: 95,
    filter: "agTextColumnFilter",
  },
  {
    field: "intermediatePoNumber",
    headerName: "IO Number",
    minWidth: 90,
    filter: "agTextColumnFilter",
  },
  {
    field: "vendorCode",
    headerName: "Vendor",
    minWidth: 110,
    filter: "agTextColumnFilter",
  },
  {
    field: "warehouseDisplay",
    headerName: "Warehouse",
    minWidth: 95,
    filter: "agTextColumnFilter",
    cellClass: "results-panel-location-cell",
  },
  {
    field: "skuCount",
    headerName: "Unique SKUs",
    minWidth: 72,
    type: "numericColumn",
    filter: "agNumberColumnFilter",
  },
  {
    field: "totalUnits",
    headerName: "Total Units",
    minWidth: 72,
    type: "numericColumn",
    filter: "agNumberColumnFilter",
  },
  {
    field: "totalCubicFeet",
    headerName: "Total Cubic Feet",
    minWidth: 95,
    type: "numericColumn",
    filter: "agNumberColumnFilter",
    valueFormatter: (params) =>
      typeof params.value === "number" ? params.value.toFixed(1) : "",
  },
  {
    field: "distributionStatus",
    headerName: "Current Distribution Status",
    minWidth: 120,
    filter: "agTextColumnFilter",
    cellRenderer: distributionStatusRenderer,
  },
  {
    field: "vendorCutDate",
    headerName: "Vendor Cut Date",
    minWidth: 95,
    filter: "agTextColumnFilter",
    valueFormatter: (params: { value: string | null }) =>
      formatNullableDate(params.value),
  },
  {
    headerName: "Shipping Date Range",
    minWidth: 115,
    filter: "agTextColumnFilter",
    valueGetter: (params) => {
      const data = params.data;
      return data && !isDetailRow(data)
        ? `${formatUsDate(data.shipDateRangeStart)} – ${formatUsDate(data.shipDateRangeEnd)}`
        : "";
    },
  },
  {
    field: "lastDistributionDate",
    headerName: "Last Distribution Date",
    minWidth: 110,
    filter: "agTextColumnFilter",
    valueFormatter: (params: { value: string }) => formatUsDate(params.value),
  },
  {
    colId: "details",
    headerName: "Details",
    minWidth: 100,
    sortable: false,
    filter: false,
    cellRenderer: detailsToggleRenderer,
  },
];

const rowSelection = {
  mode: "multiRow" as const,
  checkboxes: true,
  headerCheckbox: true,
  enableClickSelection: true,
  selectAll: "filtered" as const,
};

const selectionColumnDef: ColDef = GRID_SELECTION_COLUMN_DEF;

const defaultColDef: ColDef = FITTABLE_DEFAULT_COL_DEF;
</script>

<template>
  <section
    class="panel-card results-panel-card flex h-full min-h-0 flex-col overflow-y-auto"
  >
    <div class="panel-card-header results-panel-header !py-2.5">
      <h2 class="m-0">
        📦 Intermediate Orders
        <span v-if="hasSearched" class="font-medium text-slate-500"
          >— {{ orderRows.length }} order{{
            orderRows.length === 1 ? "" : "s"
          }}</span
        >
      </h2>
      <button
        v-if="hasSearched"
        type="button"
        class="panel-secondary-button"
        :disabled="loading"
        @click="emit('refresh')"
      >
        ⟳ Refresh
      </button>
    </div>

    <div class="results-panel-meta-strip">
      <div class="results-panel-meta-left">
        <span
          class="results-panel-section-chip results-panel-section-chip--primary"
        >
          Intermediate Orders: {{ orderRows.length }}
        </span>
      </div>
    </div>

    <div
      v-if="!hasSearched"
      class="results-panel-guidance"
      role="status"
      aria-live="polite"
    >
      <p class="results-panel-guidance__title">Before you start</p>
      <p class="results-panel-guidance__body">
        Use the <strong>Vendor Search / Bulk PO Search</strong> button above and
        the <strong>Bulk Order Lookup</strong> tab to find Intermediate Orders.
        Select <strong>View Items</strong> on any order to see its SKUs.
      </p>
    </div>

    <div
      v-if="error"
      class="intermediate-order-search-error"
      role="alert"
      aria-live="assertive"
    >
      <strong>Search failed.</strong> {{ error }} Try
      <button
        type="button"
        class="intermediate-order-search-error__retry"
        @click="emit('refresh')"
      >
        refreshing
      </button>
      or searching again.
    </div>

    <div class="min-h-[200px] min-w-0 flex-1 overflow-hidden">
      <AppGrid
        id="intermediate-order-results-grid"
        :column-defs="columnDefs"
        :row-data="rowData"
        :default-col-def="defaultColDef"
        :selection-column-def="selectionColumnDef"
        :row-selection="rowSelection"
        :is-full-width-row="isFullWidthRow"
        :full-width-cell-renderer="IntermediateOrderDetailRenderer"
        :get-row-id="getRowId"
        :get-row-height="getRowHeight"
        :get-row-class="getRowClass"
        :default-row-height="ROW_HEIGHT"
        :post-sort-rows="postSortRows"
        :enable-column-menu="true"
        :fill-container="true"
        :max-rows="100"
        :no-rows-overlay-component-params="{
          showLoadingAnimation: loading,
          getTitle: () => {
            if (loading) return 'Searching Intermediate Orders...';
            if (error) return 'Search failed';
            return !hasSearched
              ? 'Run a search to populate results.'
              : 'No Intermediate Orders found';
          },
          getSubtitle: () => {
            if (loading) return '';
            if (error) return error;
            return !hasSearched
              ? 'Use the Vendor Search / Bulk PO Search button above and the Bulk Order Lookup tab.'
              : 'No Intermediate Orders match the current search filters. Adjust your criteria and search again.';
          },
        }"
        show-top-border
        @grid-ready="onGridReady"
        @selection-changed="onSelectionChanged"
      />
    </div>
  </section>
</template>
